import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listOperationalSignals,
  listOperationalSignalFacets,
} from "@/lib/replenishment-lifecycle.functions";
import { useScope } from "@/hooks/use-scope";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

/**
 * Operational Signals — generic, cross-capability stream browser.
 *
 * This page is intentionally domain-agnostic. Today the only emitter is
 * `warehouse_replenishment`, but routing, receiving, returns, inventory
 * planning, labor, cycle counts, transfers, and billing will all emit into
 * the same `operational_signals` table. The filter dropdowns are populated
 * from whatever the tenant has actually emitted in the last 30 days.
 */
export const Route = createFileRoute("/_authenticated/app/admin/operational-signals")({
  component: OperationalSignalsPage,
});

const ANY = "__any__";
const SEVERITIES = ["info", "warn", "error", "critical"] as const;

function sevTone(s: string) {
  if (s === "critical") return "destructive" as const;
  if (s === "error") return "destructive" as const;
  if (s === "warn") return "secondary" as const;
  return "outline" as const;
}

function OperationalSignalsPage() {
  const { tenantId, facilityId } = useScope();
  const [domain, setDomain] = useState<string>(ANY);
  const [signalType, setSignalType] = useState<string>(ANY);
  const [severity, setSeverity] = useState<string>(ANY);
  const [since, setSince] = useState<string>("60");

  const listFn = useServerFn(listOperationalSignals);
  const facetsFn = useServerFn(listOperationalSignalFacets);

  const { data: facets } = useQuery({
    queryKey: ["op-signal-facets", tenantId],
    enabled: !!tenantId,
    queryFn: () => facetsFn({ data: { tenantId: tenantId! } }),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["op-signals", tenantId, facilityId, domain, signalType, severity, since],
    enabled: !!tenantId,
    refetchInterval: 10_000,
    queryFn: () => listFn({ data: {
      tenantId: tenantId!,
      facilityId: facilityId ?? undefined,
      domain: domain === ANY ? undefined : domain,
      signalType: signalType === ANY ? undefined : signalType,
      severity: severity === ANY ? undefined : severity as any,
      sinceMinutes: Number(since),
      limit: 200,
    }}),
  });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Operational Signals</h2>
        <p className="text-sm text-muted-foreground">
          Append-only stream of operational events from every capability — replenishment today,
          routing / receiving / returns / planning / labor next. Use this as raw signal; per-domain
          dashboards are built on top of it.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <FacetSelect label="Domain" value={domain} onChange={setDomain} options={facets?.domains ?? []} />
        <FacetSelect label="Signal type" value={signalType} onChange={setSignalType} options={facets?.signalTypes ?? []} />
        <FacetSelect label="Severity" value={severity} onChange={setSeverity} options={SEVERITIES as unknown as string[]} />
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Window</div>
          <Select value={since} onValueChange={setSince}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="15">Last 15 min</SelectItem>
              <SelectItem value="60">Last hour</SelectItem>
              <SelectItem value="360">Last 6 hours</SelectItem>
              <SelectItem value="1440">Last 24 hours</SelectItem>
              <SelectItem value="10080">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[170px]">When</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Signal</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Entity</TableHead>
            <TableHead>Payload</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No signals in window.</TableCell></TableRow>
            ) : rows.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="text-xs text-muted-foreground tabular-nums">{new Date(r.occurred_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs font-mono">{r.domain}</TableCell>
                <TableCell className="text-xs font-mono">{r.signal_type}</TableCell>
                <TableCell><Badge variant={sevTone(r.severity)} className="whitespace-nowrap">{r.severity}</Badge></TableCell>
                <TableCell className="text-xs"><span className="text-muted-foreground">{r.entity_type}</span> <span className="font-mono">{r.entity_id ? String(r.entity_id).slice(0, 8) : "—"}</span></TableCell>
                <TableCell className="text-xs font-mono max-w-[480px] truncate" title={JSON.stringify(r.payload)}>
                  {JSON.stringify(r.payload)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function FacetSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[180px]"><SelectValue placeholder={`Any ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any {label.toLowerCase()}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}